import React from "react";
import { useTranslation } from "react-i18next";
import { Shield, Users, UserCheck, Eye } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Constants for determining hierarchy level
const SUPER_AUTHORITIES = ["tahsildar", "ddlr", "adlr"];
const SUPERVISORS = ["revenue_inspector", "shirestedar", "deputy_tahsildar"];
const FIELD_OFFICERS = ["village_accountant", "first_division_clerk", "second_division_clerk", "surveyor"];

/**
 * Determines the hierarchy level based on user designation
 */
const getHierarchyLevel = (designation) => {
  if (!designation) return null;
  
  const lowerDesignation = designation.toLowerCase();
  
  if (SUPER_AUTHORITIES.includes(lowerDesignation)) {
    return "superAuthority";
  }
  if (SUPERVISORS.includes(lowerDesignation)) {
    return "supervisor";
  }
  if (FIELD_OFFICERS.includes(lowerDesignation)) {
    return "fieldOfficer";
  }
  
  // Default to field officer for any other designation
  return "fieldOfficer";
};

/**
 * Returns the appropriate icon for the hierarchy level
 */
const getHierarchyIcon = (level) => {
  switch (level) {
    case "superAuthority":
      return Shield;
    case "supervisor":
      return Users;
    case "fieldOfficer":
    default:
      return UserCheck;
  }
};

/**
 * Returns styling based on hierarchy level
 */
const getHierarchyStyles = (level) => {
  switch (level) {
    case "superAuthority":
      return {
        badge: "bg-amber-100 text-amber-800 border-amber-300",
        icon: "text-amber-600",
        scope: "bg-amber-50 text-amber-700 border-amber-200"
      };
    case "supervisor":
      return {
        badge: "bg-blue-100 text-blue-800 border-blue-300",
        icon: "text-blue-600",
        scope: "bg-blue-50 text-blue-700 border-blue-200"
      };
    case "fieldOfficer":
    default:
      return {
        badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
        icon: "text-emerald-600",
        scope: "bg-emerald-50 text-emerald-700 border-emerald-200"
      };
  }
};

/**
 * Returns the data scope description based on hierarchy level
 */
const getDataScope = (level) => {
  switch (level) {
    case "superAuthority":
      return "allDepartment";
    case "supervisor":
      return "subordinates";
    case "fieldOfficer":
    default:
      return "ownOnly";
  }
};

export default function HierarchyBadge({ user }) {
  const { t } = useTranslation();
  
  if (!user?.designation) return null;
  
  const level = getHierarchyLevel(user.designation);
  if (!level) return null;
  
  const Icon = getHierarchyIcon(level);
  const styles = getHierarchyStyles(level);
  const dataScope = getDataScope(level);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-help ${styles.badge}`}
            data-testid="hierarchy-badge"
          >
            <Icon className={`w-3.5 h-3.5 ${styles.icon}`} />
            <span>{t(`hierarchy.${level}`)}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-xs p-3 bg-white border shadow-lg rounded-lg"
          data-testid="hierarchy-tooltip"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${styles.icon}`} />
              <span className="font-semibold text-slate-800">
                {t(`hierarchy.${level}`)}
              </span>
            </div>
            <p className="text-xs text-slate-600">
              {t(`hierarchy.${level}Desc`)}
            </p>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${styles.scope}`}>
              <Eye className="w-3 h-3" />
              <span className="font-medium">{t('hierarchy.viewScope')}:</span>
              <span>{t(`hierarchy.${dataScope}`)}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
